'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'

const COVER_CLASSES =
  'pointer-events-none absolute left-1/2 top-1/2 aspect-video h-auto w-auto min-h-full min-w-full -translate-x-1/2 -translate-y-1/2'

/**
 * Muted autoplay loop served from Bunny Stream, sized to COVER its (relative)
 * parent like `object-cover` — the iframe keeps 16:9 and overflows whichever
 * axis the container crops, so swapping an mp4 background for this is visually
 * identical. `loading="lazy"` defers below-fold embeds natively.
 *
 * Steve 2026-08-19: Bunny's player flashes its round play button while it
 * boots, so the iframe stays at opacity 0 until the player reports playback
 * has started (Bunny embeds speak the player.js postMessage protocol), with a
 * short post-load timer as fallback. The video's first-frame thumbnail shows
 * as a poster underneath in the meantime — no white box, no button flash —
 * and the stream fades in over it for a seamless hand-off.
 *
 * Steve 2026-08-28: the iframe's `src` isn't set until an idle tick after
 * mount, so its ~700KB player bundle doesn't compete with the page's own
 * content for bandwidth right at load — see the `armed` state below.
 */
export function BunnyBgVideo({
  guid,
  className,
  priority,
}: {
  guid: string
  className?: string
  /** Set for an above-the-fold instance (e.g. the home hero) — this poster is
   * likely the page's LCP element, so it's worth the fetch-priority hint.
   * Leave unset for below-fold instances (PageSpeed audit 2026-08-28). */
  priority?: boolean
}) {
  // Library id is public (visible in every embed URL); the literal fallback
  // lets client components render the embed where server env isn't inlined.
  const libraryId = process.env.BUNNY_STREAM_LIBRARY_ID || '730812'
  const ref = useRef<HTMLIFrameElement>(null)
  const [visible, setVisible] = useState(false)
  // The embed's player chrome (plyr, hls.js, jquery) is ~700KB of JS the iframe
  // starts fetching the instant its `src` is set. On the home hero that iframe
  // is in the initial viewport, so setting `src` at mount had it compete with
  // the page's own LCP content for bandwidth on a throttled connection
  // (PageSpeed audit 2026-08-28: 10s mobile LCP). The poster `<img>` below
  // already covers this box on first paint, so deferring the src by one idle
  // tick is visually identical — the video just starts fetching a beat later.
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    const ric =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 1)
    const cancel =
      typeof window.cancelIdleCallback === 'function' ? window.cancelIdleCallback : clearTimeout
    const id = ric(() => setArmed(true))
    return () => cancel(id as number)
  }, [])

  useEffect(() => {
    if (visible) return
    const onMessage = (e: MessageEvent) => {
      if (!ref.current || e.source !== ref.current.contentWindow) return
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
        if (
          data &&
          (data.event === 'play' || data.event === 'playing' || data.event === 'timeupdate')
        ) {
          setVisible(true)
        }
      } catch {
        /* non-JSON messages are not the player's */
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [visible])

  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={`https://vz-d2636ff3-57a.b-cdn.net/${guid}/thumbnail.jpg`}
        alt=""
        aria-hidden="true"
        fetchPriority={priority ? 'high' : undefined}
        className={cn(COVER_CLASSES, 'object-cover')}
      />
      <iframe
        ref={ref}
        src={
          armed
            ? `https://iframe.mediadelivery.net/embed/${libraryId}/${guid}?autoplay=true&loop=true&muted=true&preload=true&responsive=false`
            : undefined
        }
        onLoad={() => {
          // Guard against the unarmed iframe's initial blank load (no `src`
          // yet) firing this — only the real embed load should start the timer.
          if (!armed) return
          // Fallback: reveal shortly after the player boots even if no
          // player.js event arrives (the button flash lasts well under this)
          setTimeout(() => setVisible(true), 1500)
        }}
        className={cn(
          COVER_CLASSES,
          'border-0 transition-opacity duration-500',
          visible ? 'opacity-100' : 'opacity-0',
          className,
        )}
        allow="autoplay; encrypted-media"
        loading="lazy"
        title=""
        aria-hidden="true"
        tabIndex={-1}
      />
    </>
  )
}
